import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationSchema from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";

import DocumentFilterSchema from "documents/_api/commands/_schema/document-filter-schema";
import DocumentOrderSchema from "documents/_api/commands/_schema/document-order-schema";
import DocumentQuery, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters,
} from "documents/_api/queries/document-query";
import { z } from "zod";

export type DocumentQueryJSON = z.infer<typeof DocumentQuerySchema.DocumentQueryJSONZ>;

export type DocumentQueryParams = z.infer<typeof DocumentQuerySchema.DocumentQueryZ>;

export default class DocumentQuerySchema {
  public static readonly DocumentQueryJSONZ = z.object({
    filterParameters: DocumentFilterSchema.DocumentFilterJSONZ,
    orderParameters: DocumentOrderSchema.DocumentOrderZ,
    paginationParameters: PaginationSchema.PaginationZ,
  });

  public static readonly DocumentQueryZ = z.object({
    filterParameters: DocumentFilterSchema.DocumentFilterZ,
    orderParameters: DocumentOrderSchema.DocumentOrderZ,
    paginationParameters: PaginationSchema.PaginationZ,
  });

  public static validateJSON(values: unknown): DocumentQueryJSON {
    return parseOrThrow(DocumentQuerySchema.DocumentQueryJSONZ, values, {
      logError: true,
      schemaName: "DocumentQuerySchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is DocumentQueryJSON {
    try {
      DocumentQuerySchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: DocumentQueryJSON): DocumentQuery {
    const parsed = DocumentQuerySchema.validateJSON(json);

    const filterParameters = new DocumentFilterParameters({
      name: parsed.filterParameters.name ?? undefined,
      minCreatedDate: AHDate.fromJSON(parsed.filterParameters.minCreatedDate) ?? undefined,
      maxCreatedDate: AHDate.fromJSON(parsed.filterParameters.maxCreatedDate) ?? undefined,
      query: parsed.filterParameters.query ?? undefined,
      entityClass: parsed.filterParameters.entityClass ?? undefined,
      entityId: Guid.fromJSON(parsed.filterParameters.entityId) ?? undefined,
      contexts: parsed.filterParameters.contexts ?? undefined,
      ownershipScope: parsed.filterParameters.ownershipScope ?? undefined,
      creatorId: Guid.fromJSON(parsed.filterParameters.creatorId) ?? undefined,
      creatorEntityId: Guid.fromJSON(parsed.filterParameters.creatorEntityId) ?? undefined,
      isArchived: parsed.filterParameters.isArchived ?? undefined,
    });

    const orderParameters = parsed.orderParameters
      ? new DocumentOrderParameters({
          orderBy: parsed.orderParameters.orderBy!,
          orderDir: parsed.orderParameters.orderDir!,
        })
      : new DocumentOrderParameters({
          orderBy: DocumentOrderField.Created,
          orderDir: OrderDirection.Descending,
        });

    const paginationParameters = PaginationParameters.create({
      pageIndex: parsed.paginationParameters.pageIndex,
      pageSize: parsed.paginationParameters.pageSize,
    });

    return new DocumentQuery({ filterParameters: filterParameters, orderParameters, paginationParameters });
  }

  public static serialize(query: DocumentQuery): DocumentQueryJSON {
    const { filterParameters, orderParameters, paginationParameters } = query;

    const json: DocumentQueryJSON = {
      filterParameters: {
        name: filterParameters.name,
        minCreatedDate: filterParameters.minCreatedDate?.toJSON(),
        maxCreatedDate: filterParameters.maxCreatedDate?.toJSON(),
        query: filterParameters.query,
        entityClass: filterParameters.entityClass,
        entityId: filterParameters.entityId ? filterParameters.entityId.value : undefined,
        contexts: filterParameters.contexts,
        ownershipScope: filterParameters.ownershipScope,
        creatorId: filterParameters.creatorId?.value,
        creatorEntityId: filterParameters.creatorEntityId?.value,
        isArchived: filterParameters.isArchived,
      },
      orderParameters: {
        orderBy: orderParameters.orderBy,
        orderDir: orderParameters.orderDir,
      },
      paginationParameters: {
        pageIndex: paginationParameters.pageIndex,
        pageSize: paginationParameters.pageSize,
      },
    };

    return parseOrThrow(DocumentQuerySchema.DocumentQueryJSONZ, json, {
      logError: true,
      schemaName: "DocumentQuerySchema",
      operation: "serialize",
      payloadSample: json,
    });
  }
}
